import {
  GraphQLFieldConfig,
  GraphQLInputObjectType,
  GraphQLNonNull,
  GraphQLList,
  GraphQLID,
  GraphQLString
} from 'graphql'
import { Context } from '../shared'
import {
  GenericMutationResponseType,
  GenericMutationResponse
} from '../types/generic-mutation-response'
import {
  vulnerabilityFilterArgType,
  VulnerabilityFilterArg
} from '../types/vulnerability-filter-arg'
import { mapVulnerabilityFilters } from '../endpoint-helpers/vulnerabilities'

interface SendToEmailArg {
  readonly input?: {
    readonly vulnerabilityIDs: string[]
    readonly emailAddress: string
    readonly organizationID: string
  }
}

const sendToEmailArg = {
  input: {
    type: new GraphQLInputObjectType({
      name: 'SendVulnerabilitiesToEmailMutationArg',
      fields: {
        vulnerabilityIDs: {
          description: 'A list of vulnerability IDs to send via email',
          type: new GraphQLNonNull(
            new GraphQLList(new GraphQLNonNull(GraphQLID))
          )
        },
        emailAddress: {
          description:
            'The email address of the recepient that will receive the vulnerability reports',
          type: new GraphQLNonNull(GraphQLString)
        },
        organizationID: {
          description:
            'The ID of the organization of the vulnerabilities being sent',
          type: new GraphQLNonNull(GraphQLID)
        }
      }
    })
  }
}

export const sendVulnerabilitiesToEmail: GraphQLFieldConfig<
  undefined,
  Context,
  SendToEmailArg
> = {
  type: new GraphQLNonNull(GenericMutationResponseType),
  description: 'Send one or more vulnerability reports to an email address',
  args: sendToEmailArg,
  async resolve(_source, args, context): Promise<GenericMutationResponse> {
    if (!args.input) {
      throw new Error('mutation input parameter is required')
    }

    const { emailAddress, vulnerabilityIDs, organizationID } = args.input

    await context.connector.post(`${organizationID}/orgtraces/share/email`, {
      email: emailAddress,
      traces: vulnerabilityIDs
    })

    return {
      _placeholder: true
    }
  }
}

interface SendToEmailByFiltersArg {
  readonly input?: {
    readonly filters: VulnerabilityFilterArg
    readonly emailAddress: string
    readonly organizationID: string
    readonly excludedVulnerabilities: string[]
  }
}

const sendToEmailByFiltersArg = {
  input: {
    type: new GraphQLInputObjectType({
      name: 'SendVulnerabilitiesToEmailByFiltersMutationArg',
      fields: {
        organizationID: {
          description:
            'The ID of the organization of the vulnerabilities being sent',
          type: new GraphQLNonNull(GraphQLID)
        },
        emailAddress: {
          description:
            'The email address of the recepient that will receive the vulnerability reports',
          type: new GraphQLNonNull(GraphQLString)
        },
        filters: {
          type: new GraphQLNonNull(vulnerabilityFilterArgType)
        },
        excludedVulnerabilities: {
          description: 'A list of vulnerabilities to exclude from the filter',
          type: new GraphQLNonNull(
            new GraphQLList(new GraphQLNonNull(GraphQLID))
          )
        }
      }
    })
  }
}

export const sendVulnerabilitiesToEmailByFilters: GraphQLFieldConfig<
  undefined,
  Context,
  SendToEmailByFiltersArg
> = {
  type: new GraphQLNonNull(GenericMutationResponseType),
  description:
    'Send one or more vulnerability reports to an email address using filters',
  args: sendToEmailByFiltersArg,
  async resolve(_source, args, context): Promise<GenericMutationResponse> {
    if (!args.input) {
      throw new Error('mutation input parameter is required')
    }

    const { emailAddress, filters, organizationID, excludedVulnerabilities } =
      args.input

    await context.connector.post(
      `${organizationID}/orgtraces/bulk/share/email`,
      {
        emailAddress,
        filters: {
          ...mapVulnerabilityFilters(filters),
          excludedTraces: excludedVulnerabilities
        }
      }
    )

    return {
      _placeholder: true
    }
  }
}
